import type { ContactMessage, ContactMessageStatus } from '@/types';
import i18n from '@/i18n/config';

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAtom, useAtomValue } from 'jotai';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Divider,
  Select,
  SelectItem,
  Spinner
} from '@heroui/react';
import { RiArrowLeftLine, RiMailLine, RiUserLine, RiHashtag, RiDeleteBinLine } from '@remixicon/react';

import DefaultLayout from '@/layouts/default';
import {
  contactMessageDetailIdAtom,
  contactMessageDetailQueryAtom,
  profileAtom
} from '@/store';
import { useContactMessageActions } from '@/hooks/contact-messages';
import { ContactMessageComposer, ContactMessageStatusBadge, ContactMessageThread } from '@/components/contact-messages';
import { DateTimeDisplay } from '@/components/shared/DateTimeDisplay';
import { PermissionGuard } from '@/components/shared/PermissionGuard';
import { ConfirmDeleteModal } from '@/components/shared/ConfirmDeleteModal';

const STATUS_OPTIONS: { key: ContactMessageStatus; label: string }[] = [
  { key: 'new', label: i18n.t('copy.new') },
  { key: 'in_progress', label: i18n.t('copy.in_progress') },
  { key: 'resolved', label: i18n.t('copy.resolved') },
  { key: 'archived', label: i18n.t('enums.archived') }
];

export default function ContactMessageDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const profile = useAtomValue(profileAtom);
  const [, setDetailId] = useAtom(contactMessageDetailIdAtom);
  const { data, isLoading, isError } = useAtomValue(contactMessageDetailQueryAtom);
  const message = data?.data as ContactMessage | undefined;

  const { handleReply, handleEdit, handleStatusChange, handleDelete, isReplying, isUpdatingStatus, isDeleting } = useContactMessageActions();

  // Delete modal state
  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false);

  React.useEffect(() => {
    if (id) {
      const numericId = Number(id);
      if (!Number.isNaN(numericId)) {
        setDetailId(numericId);
      }
    }

    return () => {
      setDetailId(null);
    };
  }, [id, setDetailId]);

  const [statusDraft, setStatusDraft] = React.useState<ContactMessageStatus | null>(null);

  React.useEffect(() => {
    if (message) {
      setStatusDraft(message.status);
    }
  }, [message?.status]);

  const handleStatusSelect = React.useCallback(
    async (next: ContactMessageStatus) => {
      if (!message || next === message.status) {
        return;
      }
      setStatusDraft(next);
      await handleStatusChange({ messageId: message.id, status: next });
    },
    [handleStatusChange, message]
  );

  if (isLoading) {
    return (
      <DefaultLayout>
        <div className="flex items-center justify-center p-6">
          <Spinner size="lg" />
        </div>
      </DefaultLayout>
    );
  }

  if (isError || !message) {
    return (
      <DefaultLayout>
        <div className="p-6">
          <Card>
            <CardBody className="space-y-4">
              <p className="text-danger text-lg font-semibold">{i18n.t('modals.contactMessage.loadFailed')}</p>
              <Button variant="light" onPress={() => navigate('/relationships/contact-messages')}>
                {i18n.t('common.back')}
              </Button>
            </CardBody>
          </Card>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <div className="space-y-6 p-6">
        <div className="flex flex-wrap items-center gap-4">
          <Button isIconOnly variant="light" onPress={() => navigate('/relationships/contact-messages')}>
            <RiArrowLeftLine className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{message.subject || i18n.t('modals.contactMessage.defaultSubject')}</h1>
            <p className="text-default-500 text-sm">{i18n.t('modals.contactMessage.breadcrumb', { id: message.id })}</p>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
          <Card className="h-fit">
            <CardHeader className="flex items-center justify-between">
              <div>
                <p className="text-default-500 text-sm">{i18n.t('modals.contactMessage.conversation')}</p>
                <p className="text-lg font-semibold">{i18n.t('modals.contactMessage.thread')}</p>
              </div>
              <ContactMessageStatusBadge status={message.status} readAt={message.read_at} />
            </CardHeader>
            <Divider />
            <CardBody className="space-y-4">
              <ContactMessageThread
                message={message}
                onEdit={({ messageId, body, isNote, parentMessageId }) =>
                  handleEdit({ messageId, body, isNote, parentMessageId })
                }
                currentUserId={profile?.id}
              />

              <PermissionGuard permission="ContactMessage.store">
                <ContactMessageComposer
                  isSubmitting={isReplying}
                  onSubmit={({ body, isNote }) => handleReply({ messageId: message.id, body, isNote })}
                />
              </PermissionGuard>
            </CardBody>
          </Card>

          <div className="space-y-4">
            <Card>
              <CardHeader className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-default-500 text-sm">{i18n.t('filters.status')}</p>
                  <ContactMessageStatusBadge status={message.status} readAt={message.read_at} />
                </div>
                <PermissionGuard permission="ContactMessage.update">
                  <Select
                    className="max-w-[180px]"
                    label={i18n.t('copy.change_status')}
                    selectedKeys={new Set([statusDraft ?? message.status])}
                    size="sm"
                    isDisabled={isUpdatingStatus}
                    onSelectionChange={(keys) => {
                      const value = Array.from(keys as Set<string>)[0] as ContactMessageStatus | undefined;
                      if (value) {
                        void handleStatusSelect(value);
                      }
                    }}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.key}>{option.label}</SelectItem>
                    ))}
                  </Select>
                </PermissionGuard>
              </CardHeader>
              <Divider />
              <CardBody className="space-y-4">
                <InfoRow
                  icon={<RiHashtag className="text-default-400 h-4 w-4" />}
                  label={i18n.t('copy.message_id')}
                  value={`#${message.id}`}
                />
                <InfoRow
                  icon={<RiUserLine className="text-default-400 h-4 w-4" />}
                  label={i18n.t('copy.requester')}
                  value={message.name || 'Anonymous'}
                />
                {message.email && (
                  <InfoRow
                    icon={<RiMailLine className="text-default-400 h-4 w-4" />}
                    label={i18n.t('auth.email')}
                    value={message.email}
                  />
                )}
                <InfoRow
                  icon={<RiUserLine className="text-default-400 h-4 w-4" />}
                  label={i18n.t('copy.assigned_section')}
                  value={
                    message.section ? (
                      <Chip color="secondary" size="sm" variant="flat">
                        {message.section.name}
                      </Chip>
                    ) : (
                      i18n.t('pages.contactMessages.unassigned')
                    )
                  }
                />
                <InfoRow
                  icon={<RiMailLine className="text-default-400 h-4 w-4" />}
                  label={i18n.t('copy.created')}
                  value={<DateTimeDisplay dateString={message.created_at} />}
                />
                <InfoRow
                  icon={<RiMailLine className="text-default-400 h-4 w-4" />}
                  label={i18n.t('copy.updated')}
                  value={<DateTimeDisplay dateString={message.updated_at} />}
                />
              </CardBody>
            </Card>

            {/* Danger Zone */}
            {message.is_admin && (
              <Card className="border-2 border-danger-200 bg-danger-50">
                <CardHeader className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="text-danger-700 text-sm font-semibold">{i18n.t('modals.experiment.dangerZone')}</p>
                    <p className="text-danger-600 text-xs">{i18n.t('modals.experiment.irreversibleActions')}</p>
                  </div>
                  <PermissionGuard permission="ContactMessage.destroy">
                    <Button
                      color="danger"
                      size="sm"
                      variant="flat"
                      onPress={() => setIsDeleteOpen(true)}
                      startContent={<RiDeleteBinLine className="h-4 w-4" />}
                    >
                      {i18n.t('modals.contactMessage.deleteTitle')}
                    </Button>
                  </PermissionGuard>
                </CardHeader>
              </Card>
            )}
          </div>
        </div>
      </div>

      <ConfirmDeleteModal
        isOpen={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        title={i18n.t('copy.delete_contact_message')}
        message={
          message
            ? `Are you sure you want to delete this message? This action cannot be undone and will remove the message and all its replies.`
            : 'Are you sure you want to delete this message?'
        }
        confirmText={i18n.t('common.delete')}
        cancelText="Cancel"
        isLoading={isDeleting}
        onConfirm={() => {
          if (message) {
            handleDelete({ messageId: message.id }).then(() => {
              setIsDeleteOpen(false);
              navigate('/relationships/contact-messages');
            });
          }
        }}
      />
    </DefaultLayout>
  );
}

interface InfoRowProps {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
}

function InfoRow({ icon, label, value }: InfoRowProps) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-default-500 text-sm">{label}</span>
      </div>
      <div className="text-right text-sm font-semibold">{value}</div>
    </div>
  );
}

